import {
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  InfiniteData,
} from '@tanstack/react-query'
import { FetchParams, PaginatedResponse } from './types'

type PaginateResult<T> = UseInfiniteQueryResult<InfiniteData<PaginatedResponse<T>>, Error> & {
  flatData: T[]
  totalCount: number
  pageMetadata: PaginatedResponse<T>['metadata'] | null
}
type Fetcher<T> = (params: FetchParams) => Promise<PaginatedResponse<T>>
// `keepPreviousData` keeps the last successful result on screen while a new
// one loads (e.g. switching a filter tab or a background refetch triggered by
// a push notification), so the list never flashes blank. Opt-in per caller to
// avoid changing the behaviour of every paginated list at once.
type UsePaginateOptions = { enabled?: boolean; keepPreviousData?: boolean }

export function usePaginate<T>(
  key: string,
  fetcher: Fetcher<T>,
  extraParams?: Record<string, any>,
  options?: UsePaginateOptions,
): PaginateResult<T> {
  const query = useInfiniteQuery<PaginatedResponse<T>, Error>({
    queryKey: [key, extraParams],
    queryFn: ({ pageParam }) =>
      fetcher({ pageParam: pageParam as string | undefined, ...extraParams }),
    getNextPageParam: (lastPage: PaginatedResponse<T>) => {
      const { count, limit, page } = lastPage?.metadata ?? { count: 0, limit: 0, page: 0 }
      const totalPages = Math.ceil(count / limit) - 1
      return page <= totalPages ? page + 1 : undefined
    },
    initialPageParam: '1',
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
    ...(options?.keepPreviousData && { placeholderData: keepPreviousData }),
  })
  // flatten pages for easier FlatList use
  const flatData = query.data?.pages.flatMap((page) => page.data ?? []) ?? []
  // Get total count from the first page's metadata (count is the same across all pages)
  const totalCount = query.data?.pages[0]?.metadata?.count ?? 0
  const pageMetadata = query.data?.pages[0]?.metadata ?? null

  return {
    ...query,
    flatData,
    totalCount,
    pageMetadata,
  }
}
